import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Download, RotateCcw, type LucideIcon } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import ReadOnlyNotice from "@/components/BasProcess/ReadOnlyNotice";
import { AiPolishTextarea } from "@/components/common/AiPolishTextarea";
import { AiDocumentReviewDialog } from "@/components/common/AiDocumentReviewDialog";
import { AiSuggestionHistoryDialog } from "@/components/common/AiSuggestionHistoryDialog";

export interface EditableSectionDef {
  key: string;
  title: string;
  hint: string;
  template: string;
}

interface SectionDoc {
  id: string;
  name?: string;
  version: string;
  status: string;
  sections: Record<string, string>;
}

interface Props {
  projectId: string;
  /** Tabellnamn i backend */
  table: string;
  docNumber: string;
  title: string;
  description: string;
  legalRef: string;
  icon: LucideIcon;
  documentType: string;
  sections: EditableSectionDef[];
  statusLabels: Record<string, string>;
  /** Visa namnfält (vissa dokument har fast namn) */
  withName?: boolean;
  defaultName?: string;
  onExport: (doc: SectionDoc, projectName: string) => Promise<void>;
  emptyText: string;
  /** Skrivskydd – dokumentet kan läsas och exporteras men inte ändras. */
  readOnly?: boolean;
  /** Visar ändringslogg för dokumentet */
  auditDocumentType?: string;
}

/** Gemensam redigerare för avsnittsbaserade rutiner och planer. */
export const SectionDocumentEditor: React.FC<Props> = ({
  projectId,
  table,
  docNumber,
  title,
  description,
  legalRef,
  icon,
  documentType,
  sections,
  statusLabels,
  withName = true,
  readOnly = false,
  auditDocumentType,
  defaultName,
  onExport,
  emptyText,
}) => {
  const [doc, setDoc] = useState<SectionDoc | null>(null);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const columns = withName ? "id, name, version, status, sections" : "id, version, status, sections";

  const load = async () => {
    const [{ data, error }, { data: proj }] = await Promise.all([
      supabase
        .from(table as never)
        .select(columns)
        .eq("project_id", projectId)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle(),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    if (error) toast.error("Kunde inte läsa dokumentet");
    setProjectName(proj?.name ?? "");
    const row = data as unknown as SectionDoc | null;
    setDoc(row ? { ...row, sections: (row.sections as Record<string, string>) || {} } : null);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, table]);

  const create = async () => {
    setBusy(true);
    const payload: Record<string, unknown> = {
      project_id: projectId,
      sections: Object.fromEntries(sections.map((s) => [s.key, s.template])),
    };
    if (withName && defaultName) payload.name = defaultName;
    const { data, error } = await supabase
      .from(table as never)
      .insert(payload as never)
      .select(columns)
      .single();
    setBusy(false);
    if (error || !data) return toast.error("Kunde inte skapa dokumentet");
    const row = data as unknown as SectionDoc;
    setDoc({ ...row, sections: (row.sections as Record<string, string>) || {} });
    toast.success("Dokumentet skapat med förifylld standardtext");
  };

  const update = async (patch: Partial<SectionDoc>) => {
    if (!doc) return;
    setDoc({ ...doc, ...patch });
    const { error } = await supabase
      .from(table as never)
      .update(patch as never)
      .eq("id", doc.id);
    if (error) toast.error("Kunde inte spara");
  };

  const updateSection = (key: string, value: string) => {
    if (!doc) return;
    update({ sections: { ...doc.sections, [key]: value } });
  };

  const exportWord = async () => {
    if (!doc) return;
    try {
      await onExport(doc, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const filled = doc
    ? sections.filter((s) => (doc.sections[s.key] || "").trim().length > 30).length
    : 0;
  const progress = Math.round((filled / sections.length) * 100);
  const Icon = icon;

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber={docNumber}
        exportHistory={{ documentType, projectId }}
        audit={
          auditDocumentType
            ? { documentType: auditDocumentType, projectId, title: `Ändringslogg – ${title}` }
            : undefined
        }
        title={title}
        description={description}
        legalRef={legalRef}
        icon={icon}
        meta={
          doc ? (
            <div className="flex flex-wrap items-center gap-4">
              <Badge variant={doc.status === "faststalld" ? "default" : "outline"}>
                {statusLabels[doc.status] || doc.status}
              </Badge>
              <div className="w-40">
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span>Ifyllda avsnitt</span>
                  <span>
                    {filled}/{sections.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </div>
          ) : null
        }
        actions={
          doc ? (
            <div className="flex flex-wrap items-center gap-2">
              {!readOnly && (
                <AiDocumentReviewDialog
                  documentName={title}
                  documentType={documentType}
                  documentId={doc.id}
                  projectId={projectId}
                  collectFields={() =>
                    sections.map((s) => ({
                      id: s.key,
                      label: s.title,
                      text: doc.sections[s.key] ?? "",
                    }))
                  }
                  applyFields={(updates) => {
                    const next = { ...doc.sections };
                    updates.forEach((u) => {
                      next[u.id] = u.text;
                    });
                    update({ sections: next });
                  }}
                />
              )}
              <AiSuggestionHistoryDialog documentType={documentType} documentId={doc.id} />
              <Button size="sm" variant="outline" onClick={exportWord}>
                <Download className="mr-1 h-4 w-4" /> Word
              </Button>
            </div>
          ) : null
        }
      />

      {readOnly && <ReadOnlyNotice />}

      {!doc ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <Icon className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mx-auto max-w-md text-sm text-muted-foreground">{emptyText}</p>
            <Button onClick={create} disabled={busy || readOnly}>
              {busy ? "Skapar..." : "Skapa dokument"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <fieldset disabled={readOnly} className="space-y-4 disabled:opacity-90">
          <Card>
            <CardContent className="grid gap-2 pt-6 sm:grid-cols-[1fr_120px_auto]">
              {withName ? (
                <Input
                  key={`n-${doc.id}`}
                  defaultValue={doc.name ?? ""}
                  placeholder="Dokumentnamn"
                  onBlur={(e) => update({ name: e.target.value })}
                />
              ) : (
                <div className="text-sm font-medium">{title}</div>
              )}
              <Input
                key={`v-${doc.id}`}
                defaultValue={doc.version}
                placeholder="Version"
                onBlur={(e) => update({ version: e.target.value })}
              />
              <div className="flex flex-wrap gap-2">
                {Object.keys(statusLabels).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={doc.status === s ? "default" : "outline"}
                    onClick={() => update({ status: s })}
                  >
                    {statusLabels[s]}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {sections.map((s) => (
            <Card key={s.key}>
              <CardContent className="space-y-2 pt-6">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-semibold">{s.title}</h2>
                    <p className="text-xs text-muted-foreground">{s.hint}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      updateSection(s.key, s.template);
                      toast.success("Standardtexten återställd");
                    }}
                  >
                    <RotateCcw className="mr-1 h-4 w-4" /> Återställ standardtext
                  </Button>
                </div>
                <AiPolishTextarea
                  key={`${doc.id}-${s.key}`}
                  rows={6}
                  defaultValue={doc.sections[s.key] ?? ""}
                  placeholder={s.template}
                  context={`${title} – ${s.title}`}
                  disabled={readOnly}
                  documentType={documentType}
                  documentId={doc.id}
                  projectId={projectId}
                  fieldKey={s.key}
                  fieldLabel={s.title}
                  onCommit={(v) => {
                    if (v !== (doc.sections[s.key] ?? "")) updateSection(s.key, v);
                  }}
                />
              </CardContent>
            </Card>
          ))}
        </fieldset>
      )}
    </div>
  );
};

export default SectionDocumentEditor;
